import { last, castArray } from 'lodash-es'
import { dayjsInit } from '@ow/utils'
import {
  fetchUserStatus,
  fetchFootPrinter,
  fetchMultiFootPrinter,
  fetchUserList,
  fetchLastInspectTime,
  fetchInspectStatistics,
} from '@vt/apis/lib-ex/v2/footPrinter'
import { fetchTableData } from '@vt/apis/lib-ex/v2/tables'
import Base from './base'

const ProjectCustomConfig = {
  // 沈阳
  syhjws: {
    needInspectCase: true,
    statisticSLug: 'anjianliebiao_f958f59d_da44_4ca5_9146_d2ceed78ecd7',
    footPrinterSlug: 'qiandaobiao_8697b32c_07ea_4faf_926e_bd67ea6caab1',
    isInspect: (item) => item.fromCase,
    isSignIn: (item) => !item.fromCase,
  },
  // 海淀
  hdhjjs: {
    needInspectCase: true,
    statisticSLug: 'anjianliebiao_da13817e_0563_46e8_a31e_16e8db6b352c',
    footPrinterSlug: 'anjianliebiao_da13817e_0563_46e8_a31e_16e8db6b352c',
    isInspect: true,
    realTime: true,
    isMulti: true,
  },
  // 大连1081
  dljgpt: {
    statisticSLug: 'anjianliebiao_cfbb560d_ff53_409b_a07f_29da868315bd',
    footPrinterSlug: 'anjianliebiao_cfbb560d_ff53_409b_a07f_29da868315bd',
    isInspect: true,
    realTime: true,
    isMulti: true,
  },
}

const getMaxPage = (data = []) => {
  return Math.max(...data.map((item) => item.totalPages))
}

const mergeData = (first, others = []) => {
  others.forEach((item) => {
    item.forEach((path, index) => {
      if (first[index]?.content) {
        first[index].content.push(...path.content)
      }
    })
  })
}

export default class FootPrinter extends Base {
  constructor(appId, tbNameId, vtProject) {
    super(appId, tbNameId, vtProject)
    this.idFiledName = 'objectId'
    this.dbSpecPrefix = 'tbi_footPrinter'
  }

  getCustomConfig() {
    return ProjectCustomConfig[this.vtProject.getCusKey()]
  }

  getTbIdByTbSlug(tbSlug) {
    const tbs = Object.values(this.tbs)

    return tbs.find((tb) => tb.tbSlug === tbSlug)
  }

  // 获取用户轨迹，interval为轮询间隔，非必传，为0时为禁止
  async fetchFootPrinter(body) {
    const customConfig = this.getCustomConfig()
    let markerList = []

    const sortTrack = (n1, n2) => n1.createdAt - n2.createdAt

    // 定制内容
    if (customConfig) {
      const { footPrinterSlug, isInspect, isSignIn, realTime } = customConfig
      const tb = this.getTbIdByTbSlug(footPrinterSlug)

      const { createAt, userId, checkItem, district } = body
      const [start, end] = Array.isArray(createAt)
        ? createAt
        : [createAt, createAt]

      const params = {
        queryType: 'and',
        childQueries: [
          {
            queryType: 'just',
            yacQueryOperate: {
              left: { slug: 'createdAt' },
              operator: 'gte',
              right: { value: dayjsInit(start).startOf('day').valueOf() },
            },
          },
          {
            queryType: 'just',
            yacQueryOperate: {
              left: { slug: 'createdAt' },
              operator: 'lte',
              right: { value: dayjsInit(end).endOf('day').valueOf() },
            },
          },
          {
            queryType: 'just',
            yacQueryOperate: {
              operator: 'equal',
              left: { slug: 'createdBy' },
              right: { value: userId },
            },
          },
        ],
        size: 1000,
        page: 0,
      }

      if (checkItem) {
        params.childQueries.push({
          queryType: 'just',
          yacQueryOperate: {
            operator: 'equal',
            left: { slug: 'checkItem' },
            right: { value: checkItem },
          },
        })
      }

      if (district) {
        params.childQueries.push({
          queryType: 'just',
          yacQueryOperate: {
            operator: 'like',
            left: { slug: 'district' },
            right: { value: district },
          },
        })
      }

      const { tbData: signData = [] } = await fetchTableData(
        this.appId,
        tb.tbId,
        null,
        params
      )

      const track = signData.map((item) => {
        const [lng, lat] = item.lnglat
        return {
          ...item,
          lng,
          lat,
          isInspect:
            typeof isInspect === 'function' ? isInspect(item) : isInspect,
          isSignIn: typeof isSignIn === 'function' ? isSignIn(item) : isSignIn,
        }
      })

      if (realTime) {
        // 如果需要实时轨迹，则继续fetchFootPrinter
        markerList = track
      } else {
        // 如果没有实时定位，直接返回上报案件的轨迹
        return [track.sort(sortTrack)]
      }
    }

    const { userId, createAt } = body
    try {
      const insertInspectCase = (content) => {
        if (!content.length) return
        // 如果已经有上报点位，则不用在实时轨迹上显示签到点位
        if (!customConfig?.needInspectCase) {
          content[0].isSignIn = true
          last(content).isSignIn = true
        }

        content.forEach((item) => {
          item.createdAt = dayjsInit(
            item.inspectedAt || item.createAt
          ).valueOf()
        })
      }

      const params = {
        traceType: 'FOOTPRINTER',
        pageable: false,
        userId,
        createAt: dayjsInit(last(castArray(createAt)))
          .startOf('day')
          .format('YYYY-MM-DD HH:mm:ss'),
        size: 1000,
      }

      if (customConfig?.isMulti) {
        const res = await fetchMultiFootPrinter(this.appId, params)

        const maxPages = getMaxPage(res.data)

        if (maxPages > 1) {
          const promises = []
          for (let i = 2; i <= maxPages; i++) {
            promises.push(
              fetchMultiFootPrinter(this.appId, {
                ...params,
                page: i - 1,
              })
            )
          }
          const results = await Promise.all(promises)
          mergeData(
            res.data,
            results.map((item) => item.data)
          )
        }

        const contents = (res.data || []).map((item) => {
          const { content = [] } = item
          insertInspectCase(content)
          return content
        })

        contents.push(
          markerList.map((item) => ({
            ...item,
            scatter: true,
          }))
        )

        return contents
      }

      const res = await fetchFootPrinter(this.appId, params)

      const { content = [] } = res.data || {}

      // 如果已经有上报点位，则不用在实时轨迹上显示签到点位
      insertInspectCase(content)

      // 根据时间merge检查点位
      if (markerList) {
        return [content.concat(markerList).sort(sortTrack)]
      }

      return [content]
    } catch (err) {
      return [markerList.sort(sortTrack)]
    }
  }

  // 获取用户状态，interval为轮询间隔，非必传，为0时为禁止
  async fetchUserStatus(body) {
    const userStatus = await fetchUserStatus(this.appId, body)
    // 获取最后上报时间
    const customConfig = this.getCustomConfig()

    if (customConfig) {
      const { statisticSLug } = customConfig
      const caseTb = this.getTbIdByTbSlug(statisticSLug)

      if (caseTb && userStatus.data) {
        const { userId } = body
        const lastInspectTimes = await fetchLastInspectTime(
          this.appId,
          caseTb.tbId,
          {
            userId,
            queryDsl: {},
          }
        )

        userStatus.data.forEach((item) => {
          item.lastInspectTime = lastInspectTimes[item.userId]
        })
      }
    }

    return userStatus
  }

  // 获取用户上报统计
  fetchInspectStatistics(userId) {
    const customConfig = this.getCustomConfig()

    if (customConfig) {
      const { statisticSLug } = customConfig
      const caseTb = this.getTbIdByTbSlug(statisticSLug)

      if (caseTb) {
        return fetchInspectStatistics(this.appId, caseTb.tbId, {
          queryType: 'and',
          childQueries: [
            {
              queryType: 'just',
              yacQueryOperate: {
                operator: 'equal',
                left: {
                  slug: 'createdBy',
                },
                right: {
                  value: userId,
                },
              },
            },
          ],
          size: 50,
          page: 0,
        })
      }
    }

    return null
  }

  /**
   * 获取用户列表
   * @param {*} orgId 非必传，传递返回指定组织的用户列表
   * @returns
   */
  fetchUsers(orgId) {
    return fetchUserList(this.appId, orgId).then((res) => {
      return res
    })
  }
}

FootPrinter.modelName = 'footPrinter'
